import type { Container } from '~/components/Container';
import type { InfoWithIconGrid } from '~/components/InfoWithIconGrid';
import type { CondoCtaCardSection } from '~/components/Sections/CondoCtaCardSection';
import type { CondoHeroSection } from '~/components/Sections/CondoHeroSection';
import type { CondoLocationSection } from '~/components/Sections/CondoLocationSection';
import type { ShallowRef } from 'vue';

import { SECTION_TYPES } from '~/components/ContextualHeader';

import { useEvents } from './events/useEvents';

interface CondoContextualMenu {
	templateElements: {
		heroSectionEl: Ref<HTMLElement>;
		overviewEl: Ref<HTMLElement>;
		condoInfoGridEl: Ref<HTMLElement>;
		characteristicsEl: Readonly<ShallowRef<HTMLElement | null>>;
		locationEl: Readonly<ShallowRef<HTMLElement | null>>;
		contactEl: Readonly<ShallowRef<HTMLElement | null>>;
		ctaCardEl: Readonly<ShallowRef<HTMLElement | null>>;
		surroundingPropertiesRef: Readonly<ShallowRef<HTMLElement | null>>;
	};
}

export const useCondoContextualMenu = (): CondoContextualMenu => {
	const heroSectionRef =
		useTemplateRef<InstanceType<typeof CondoHeroSection>>('heroSectionRef');
	const overviewRef =
		useTemplateRef<InstanceType<typeof Container>>('overviewRef');
	const condoInfoGridRef =
		useTemplateRef<InstanceType<typeof InfoWithIconGrid>>('condoInfoGridRef');
	const characteristicsRef = useTemplateRef<HTMLElement>('characteristicsRef');
	const locationRef =
		useTemplateRef<InstanceType<typeof CondoLocationSection>>('locationRef');
	const contactRef = useTemplateRef<HTMLElement>('contactRef');
	const ctaCardRef =
		useTemplateRef<InstanceType<typeof CondoCtaCardSection>>('ctaCardRef');
	const surroundingPropertiesRef = useTemplateRef<HTMLElement>(
		'surroundingPropertiesRef'
	);
	const exclusivePropertiesRef = useTemplateRef<HTMLElement>(
		'exclusivePropertiesRef'
	);
	const condoTypologiesRef = useTemplateRef<HTMLElement>('condoTypologiesRef');

	const heroSectionEl = computed(
		() => heroSectionRef.value?.$el as HTMLElement
	);
	const ctaCardEl = computed(() => ctaCardRef.value?.$el as HTMLElement);
	const overviewEl = computed(() => overviewRef.value?.$el as HTMLElement);
	const locationEl = computed(() => locationRef.value?.$el as HTMLElement);

	const condoInfoGridEl = computed(
		() => condoInfoGridRef.value?.$el as HTMLElement
	);

	const sectionRefs = computed(() => [
		overviewEl,
		exclusivePropertiesRef,
		condoTypologiesRef,
		condoInfoGridEl,
		characteristicsRef,
		locationEl,
		contactRef,
		ctaCardEl
	]);

	useContextualHeaderVisibilityControl({
		heroSectionRef: heroSectionEl,
		sectionRefs,
		anchorSection: surroundingPropertiesRef
	});

	const { trackMenuCondoClicked } = useEvents();

	const tabClickedCleanup = useEventBus<string>('tab-clicked').on(tabId => {
		trackMenuCondoClicked({
			click_attributes: tabId.includes(SECTION_TYPES.CHARACTERISTICS),
			click_location: tabId.includes(SECTION_TYPES.LOCATION),
			click_overview: tabId.includes(SECTION_TYPES.OVERVIEW),
			click_service: tabId.includes(SECTION_TYPES.CONTACT),
			click_exclusivities: tabId.includes(SECTION_TYPES.EXCLUSIVITIES),
			click_condo: tabId.includes(SECTION_TYPES.CONDO),
			click_sell: tabId.includes(SECTION_TYPES.SELL)
		});
	});

	onUnmounted(() => {
		tabClickedCleanup();
	});

	return {
		templateElements: {
			heroSectionEl,
			overviewEl,
			condoInfoGridEl,
			characteristicsEl: characteristicsRef,
			locationEl,
			contactEl: contactRef,
			ctaCardEl,
			surroundingPropertiesRef
		}
	};
};
